import { CHOICE_LIST_STATUS, getFooterFieldsCachedKey } from "models/cachedChoiceList";
import { addCustomChoices } from "scenes/Customization/utils";
import { choiceListsAPI } from "services/choiceLists";
import { parseServiceErrors } from "services/encounter/serviceErrors";
import { practicesAPI } from "services/practices";

import * as actionTypes from "../constants";
import { DomainType } from "../models";

import { addErrors } from "./errors";

import type { AnyAction } from "redux";
import type { ThunkAction } from "redux-thunk";
import type { ChoiceListsState, IdDescriptionBase } from "models/patientEncounter";
import type { State } from "reducers";
import type { DomainTypeEnum } from "../models";

// loadingKey - indicate key to trigger loading for different choice lists
export const getFacilitiesChoiceListAction =
  (clearPreviousListOnError = true, loadingKey?: string) =>
  (dispatch) => {
    dispatch(getChoiceListStart(loadingKey));
    choiceListsAPI
      .getFacilities()
      .then((facilities) => {
        dispatch(getFacilitiesCompleted(facilities, DomainType.Facility));
        dispatch(getChoiceListEnd(loadingKey));
      })
      .catch((reason: Error) => {
        if (clearPreviousListOnError) {
          dispatch(getFacilitiesError(reason.message));
        }

        const errors = parseServiceErrors(reason, "Cannot Load Facilities List");
        dispatch(addErrors(errors));
        dispatch(getChoiceListEnd(loadingKey));
      });
  };

// loadingKey - indicate key to trigger loading for different choice lists
export const getPracticeChoiceListAction =
  (clearPreviousListOnError = true, loadingKey = "practiceList") =>
  (dispatch) => {
    dispatch(getChoiceListStart(loadingKey));
    practicesAPI
      .getPracticeListDropDown()
      .then((practices) => {
        dispatch(getFacilitiesCompleted(practices, DomainType.Practice));
        dispatch(getChoiceListEnd(loadingKey));
      })
      .catch((reason: Error) => {
        if (clearPreviousListOnError) {
          dispatch(getFacilitiesError(reason.message));
        }

        const errors = parseServiceErrors(reason, "Cannot Load Practice List");
        dispatch(addErrors(errors));
        dispatch(getChoiceListEnd(loadingKey));
      });
  };

export const getCustomFacilitiesChoiceListAction = (loadingKey?: string) => (dispatch) => {
  dispatch(getChoiceListStart(loadingKey));
  return choiceListsAPI
    .getFacilities()
    .then((facilities) => {
      const customAll = addCustomChoices(facilities); // includes all four custom facilities
      const custom = addCustomChoices(facilities, false); // includes only two custom facilities (ALL and PERSONAL)
      dispatch(getCustomFacilitiesCompleted(facilities, customAll, custom));
      dispatch(getChoiceListEnd(loadingKey));
      return facilities;
    })
    .catch((reason: Error) => {
      dispatch(getCustomFacilitiesError(reason.message));

      const errors = parseServiceErrors(reason, "Cannot Load Facilities List");
      dispatch(addErrors(errors));
      dispatch(getChoiceListEnd(loadingKey));
      return [];
    });
};

// facilitites updating by timer
export const updateFacilitiesAction = () => (dispatch) => {
  choiceListsAPI
    .getFacilities()
    .then((facilities) => {
      dispatch(getFacilitiesCompleted(facilities));
    })
    .catch(() => {
      // ignore errors during updating. Keep previous state
      console.log("Cannot update facilities");
    });
};

export const getChoiceListStart = (loadingKey) => ({
  payload: loadingKey,
  type: actionTypes.GET_CHOICE_LIST_START,
});

export const getChoiceListEnd = (loadingKey) => ({
  payload: loadingKey,
  type: actionTypes.GET_CHOICE_LIST_END,
});

export const getFacilitiesCompleted = (
  facilities: IdDescriptionBase[],
  domain: DomainTypeEnum = DomainType.Facility,
) => ({
  payload: { facilities, domain },
  type: actionTypes.GET_FACILITIES_COMPLETED,
});

const getCustomFacilitiesCompleted = (
  facilities: IdDescriptionBase[],
  customAll: IdDescriptionBase[],
  custom: IdDescriptionBase[],
) => ({
  payload: { facilities, customAll, custom },
  type: actionTypes.GET_CUSTOM_FACILITIES_COMPLETED,
});

const getCustomFacilitiesError = (errorMsg: string) => ({
  error: errorMsg,
  type: actionTypes.GET_CUSTOM_FACILITIES_FAIL,
});

export const getFacilitiesError = (errorMsg: string) => ({
  error: errorMsg,
  type: actionTypes.GET_FACILITIES_FAIL,
});

export const getConversionFactorsTypesAction =
  (): ThunkAction<void, State, unknown, AnyAction> => async (dispatch) => {
    try {
      dispatch(getChoiceListStart("loadingConversionFactorTypes"));

      const response = await choiceListsAPI.getConversionFactorTypes();

      dispatch(
        getSingleChoiceListCompleted([
          {
            name: "conversionFactorTypes",
            values: response.map((item) => ({
              ...item,
              ViewId: item.Id,
              id: item.Id,
              title: `${item.Id} - ${item.Description}`,
            })),
          },
        ]),
      );
    } catch (error) {
      const message = "Cannot Load Conversion Factor Types";
      const errors = parseServiceErrors(error as Error, message);

      dispatch(addErrors(errors));
      dispatch(getSingleChoiceListError("conversionFactorTypes"));
    } finally {
      dispatch(getChoiceListEnd("loadingConversionFactorTypes"));
    }
  };

export const getSingleChoiceListError = (name: string) => ({
  payload: name,
  type: actionTypes.GET_CHOICELIST_FAIL,
});

export const getSingleChoiceListCompleted = (loadedLists) => ({
  payload: loadedLists,
  type: actionTypes.GET_CHOICELIST_SUCCESS,
});

const getCachedChoiceListCompleted = (key: string, data) => ({
  payload: { key, data },
  type: actionTypes.CACHED_CHOICELIST_LOADED,
});

const getCachedChoiceListStartLoading = (key: string) => ({
  payload: { key, status: CHOICE_LIST_STATUS.LOADING },
  type: actionTypes.CACHED_CHOICELIST_CHANGE_STATUS,
});

const getCachedChoiceListError = (key: string) => ({
  payload: { key, status: CHOICE_LIST_STATUS.ERROR },
  type: actionTypes.CACHED_CHOICELIST_CHANGE_STATUS,
});

export const loadFooterFieldsList =
  (grouperType: string, pricerType: string, choiceLists: ChoiceListsState) => (dispatch) => {
    const key = getFooterFieldsCachedKey(grouperType, pricerType);

    // use already loaded list
    const cachedList = choiceLists.cachedLists[key];
    if (cachedList?.status === CHOICE_LIST_STATUS.READY) {
      return;
    }

    dispatch(getCachedChoiceListStartLoading(key));
    choiceListsAPI
      .loadFooterFieldsList(grouperType, pricerType)
      .then((fields) => {
        dispatch(getCachedChoiceListCompleted(key, fields));
      })
      .catch((reason: Error) => {
        const errors = parseServiceErrors(reason, "Cannot Load Footer Fields");
        dispatch(addErrors(errors));
        dispatch(getCachedChoiceListError(key));
      });
  };
